import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useDebounce from "../../hooks/useDebounce";
import useFilterDropdowns from "../../hooks/useFilterDropdowns";
import useUrlFilters from "../../hooks/useUrlFilters";
import {
  BarChart2,
  Eye,
  Store,
  Loader2,
  AlertCircle,
  RefreshCw,
  FileText,
} from "lucide-react";
import http from "../../utils/axios";
import { downloadCommissionInvoice } from "../../store/slices/adminReportSlice";
import { showApiError, showApiSuccess } from "../../utils/toast";
import AdminFilterBar from "../../components/ui/AdminFilterBar";
import Pagination from "../../components/tables/Pagination";

// ─── constants ───────────────────────────────────────────────────────────────

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;
const defaultMonth = currentMonth === 1 ? 12 : currentMonth - 1;
const defaultYear = currentMonth === 1 ? currentYear - 1 : currentYear;

const DEFAULT_FILTERS = {
  month: defaultMonth,
  year: defaultYear,
  district_id: "",
  taluka_id: "",
  category_id: "",
  sub_category_id: "",
  search: "",
};

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmt = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount || 0);

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminReports() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { invoiceLoadingMap } = useSelector((state) => state.adminReport);

  // ── filters (synced with URL) ──────────────────────────────────────────────
  const { filters, updateFilter, resetFilters, currentPage, setPage } = useUrlFilters(
    { ...DEFAULT_FILTERS, per_page: 15 },
    { numberFields: ["month", "year", "per_page"] }
  );

  const debouncedSearch = useDebounce(filters.search);

  // ── dropdown data (reusable hook) ──────────────────────────────────────────
  const dropdowns = useFilterDropdowns({
    district_id: filters.district_id,
    category_id: filters.category_id,
  });

  // ── state ──────────────────────────────────────────────────────────────────
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── fetch report ───────────────────────────────────────────────────────────
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append("month", filters.month);
      params.append("year", filters.year);
      params.append("page", currentPage);
      params.append("per_page", filters.per_page);
      if (filters.district_id) params.append("district_id", filters.district_id);
      if (filters.taluka_id) params.append("taluka_id", filters.taluka_id);
      if (filters.category_id) params.append("category_id", filters.category_id);
      if (filters.sub_category_id) params.append("sub_category_id", filters.sub_category_id);

      const res = await http.get(`/dashboard/reports?${params.toString()}`);
      const payload = res?.data || res;
      setData(payload?.data || []);
      setPagination(payload?.pagination || null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch reports");
      setData([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [filters.month, filters.year, filters.district_id, filters.taluka_id, filters.category_id, filters.sub_category_id, filters.per_page, currentPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // ── filter actions ─────────────────────────────────────────────────────────
  const handlePageChange = (page) => {
    setPage(page);
  };

  const handleViewTransactions = (businessUuid) => {
    navigate(`/reports/${businessUuid}`);
  };

  const handleDownloadInvoice = useCallback(async (businessUuid) => {
    const result = await dispatch(downloadCommissionInvoice({
      businessUuid,
      month: filters.month,
      year: filters.year,
    }));
    if (downloadCommissionInvoice.fulfilled.match(result)) {
      showApiSuccess("Invoice downloaded successfully");
    } else {
      showApiError(null, result.payload || "Invoice not available for this period");
    }
  }, [dispatch, filters.month, filters.year]);

  // Client-side search filter on top of API data
  const filteredData = data.filter((row) => {
    if (!debouncedSearch.trim()) return true;
    const term = debouncedSearch.toLowerCase();
    const contactStr = Array.isArray(row.contact_number)
      ? row.contact_number.join(" ")
      : String(row.contact_number || "");
    return (
      (row.business_name || "").toLowerCase().includes(term) ||
      contactStr.toLowerCase().includes(term)
    );
  });

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <BarChart2 className="h-6 w-6 sm:h-7 sm:w-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Reports</h1>
              <p className="text-xs sm:text-sm text-gray-500">Business commission reports — select a business to view transactions</p>
            </div>
          </div>
          <button
            onClick={fetchReport}
            disabled={loading}
            className="self-end sm:self-auto p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Filters + Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {/* Filters */}
        <div className="p-4 border-b border-gray-200">
          <AdminFilterBar
            filters={filters}
            onFilterChange={updateFilter}
            onReset={resetFilters}
            dropdowns={dropdowns}
            showFields={["year", "month", "district", "taluka", "category", "sub_category", "search"]}
            searchPlaceholder="Business name or contact…"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            <span>Loading reports…</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <AlertCircle className="h-10 w-10 text-red-400" />
            <p className="text-gray-600">{error}</p>
            <button onClick={fetchReport} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors">
              Try Again
            </button>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Store className="h-12 w-12 text-gray-300" />
            <p className="text-gray-500 font-medium">No businesses found</p>
            {filters.search && <p className="text-gray-400 text-sm">Try a different search term</p>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["#", "Business Name", "Contact No", "District", "Taluka", "Category", "Total Revenue", "Discount Given", "Commission", "Actions"].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredData.map((row, idx) => (
                  <tr key={row.business_uuid} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-400 font-mono text-xs">
                      {(pagination
                        ? (pagination.current_page - 1) * pagination.per_page
                        : 0) + idx + 1}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900 whitespace-nowrap">{row.business_name}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                      {Array.isArray(row.contact_number)
                        ? row.contact_number[0] || "—"
                        : row.contact_number || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                      {row.district?.name || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                      {row.taluka?.name || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                      {row.category?.name || "—"}
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">
                      {fmt(row.total_revenue)}
                    </td>
                    <td className="px-4 py-3 text-yellow-600 whitespace-nowrap">
                      {fmt(row.discount_to_customers)}
                    </td>
                    <td className="px-4 py-3 font-bold text-orange-600 whitespace-nowrap">
                      {fmt(row.connectri_commission)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewTransactions(row.business_uuid)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          View Transactions
                        </button>
                        <button
                          onClick={() => handleDownloadInvoice(row.business_uuid)}
                          disabled={invoiceLoadingMap[row.business_uuid]}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors disabled:opacity-50"
                        >
                          <FileText className={`h-3.5 w-3.5 ${invoiceLoadingMap[row.business_uuid] ? "animate-pulse" : ""}`} />
                          {invoiceLoadingMap[row.business_uuid] ? "Generating…" : "Generate Invoice"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.last_page > 1 && (
          <Pagination
            currentPage={pagination.current_page}
            totalPages={pagination.last_page}
            totalItems={pagination.total}
            itemsPerPage={pagination.per_page}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
}
